// @flow
import { has } from "../../utils/helpers/objects";

import BaseModel from "../base/BaseModel";
import AttributeCollection from "../attributes/AttributeCollection";
import CompositeAttributeModel from "../attributes/CompositeAttributeModel";
import ContentConfiguration from "../contentconfiguration/ContentConfiguration";
import ErrorCollection from "../error/ErrorCollection";
import { IllegalArgumentException } from "../../exceptions";

import type {
  AttributeType,
  ModularUIModel,
  FormErrorAnchor,
  ModelOptions,
} from "../types";

import LayoutHintCollection from "../layouthint/LayoutHintCollection";

import type LinkModel from "../links/LinkModel";
import type ErrorResponse from "../error/ErrorResponse";
import type { MessageParameters } from "../../i18n";

type ErrorObject = {
  anchor: {
    objectid: string,
    elementid?: string,
  },
  id: string,
  message: string,
  properties: MessageParameters,
  layouthint: Array<string>,
};

/**
 * Form Object
 */
export default class FormObjectModel extends BaseModel {
  _attributeCollection: AttributeCollection;
  _contentConfiguration: ContentConfiguration;
  _errorCollection: ErrorCollection;
  _repeatIndex: number;
  _dynamicValidationsLoaded: boolean;

  /**
   * Construct FormObjectModel
   */
  constructor(
    object: Object,
    objectContributions: Object,
    modelOptions?: ModelOptions,
  ) {
    super(object, objectContributions, modelOptions);

    if (object && this.getElements().length > 0) {
      this._attributeCollection = new AttributeCollection(
        this.getElements(),
        this.getApplicableAttributeContributions(),
        { ...modelOptions, isReadonly: false },
      );
    } else {
      this._attributeCollection = new AttributeCollection();
    }

    this._contentConfiguration = new ContentConfiguration(
      objectContributions ? objectContributions.content : {},
    );

    this._errorCollection = new ErrorCollection("formobject");

    if (has(this.data, "referenceDate")) {
      this.attributeCollection.setReferenceDate(this.getData("referenceDate"));
    }

    this.attributeCollection.indicateContentConfiguration(
      this.contentConfiguration,
    );
  }

  /**
   */
  static createEmpty(formObjectModel: FormObjectModel): FormObjectModel {
    if (!formObjectModel) {
      throw new IllegalArgumentException(
        "createEmpty method needs a FormObjectModel as input argument to create a new version of the object",
      );
    }
    return new FormObjectModel(
      formObjectModel.data,
      formObjectModel.contributions,
      formObjectModel.modelOptions,
    );
  }

  /**
   */
  equals(object: ?FormObjectModel, withRepeatIndex: boolean = true): boolean {
    if (!object || this.key !== object.key) {
      return false;
    }

    const objectContainsOneOfTheAttributes = this.attributeCollection.all
      .filter((attribute) => !attribute.isResult)
      .some((attribute) =>
        object?.attributeCollection.hasAttributeByKey(attribute.key),
      );

    const hasSameRepeatIndex = withRepeatIndex
      ? this.repeatIndex === object.repeatIndex
      : true;

    return hasSameRepeatIndex && objectContainsOneOfTheAttributes;
  }

  /**
   */
  getAttributeByAttribute(attribute: AttributeType): AttributeType | null {
    return this.attributeCollection.getAttributeByAttribute(attribute);
  }

  /**
   */
  getAttributeByKey(key: string): AttributeType | null {
    return this.attributeCollection.getAttributeByKey(key);
  }

  /**
   */
  hasAttributeByKey(key: string): boolean {
    return this.attributeCollection.hasAttributeByKey(key);
  }

  /**
   * Get elements from both the missing attributes and the result attributes
   */
  getElements(): Array<Object> {
    const elements = [];

    if (this.data.elements) {
      elements.push(...this.data.elements);
    }

    if (this.data.results) {
      const dataResults = this.data.results.map((result) => ({
        ...result,
        isResult: true,
      }));

      elements.push(...dataResults);
    }

    if (this.data.elementid) {
      elements.push({
        ...this.data,
      });
    }

    return elements;
  }

  /**
   * Map available contributions on the available data. Only use contributions that are needed for the data
   */
  getApplicableAttributeContributions(): Array<Object> {
    if (this.data && this.contributions) {
      const dataElementIds = this.getElements().map(
        (element) => element.elementid,
      );

      const contributions = this.getAttributesInData(
        dataElementIds,
        this.contributions.attributes,
      );

      // set all attribute mandatory for dynamic object
      if (this.isDynamic) {
        return contributions.map((attribute) => {
          const [attributeKey] = Object.keys(attribute);
          return {
            [attributeKey]: {
              ...attribute[attributeKey],
              mandatory: true,
            },
          };
        });
      }

      return contributions;
    }

    return [];
  }

  /**
   * Recursevily check if an attribute id occurs in the tree of attribute contributions.
   * The complete leaf of the tree is returned when an attribute id matches
   */
  getAttributesInData(
    dataElementIds: Array<string>,
    attributesContributions: Object,
  ): Array<Object> {
    return attributesContributions.filter((attributeContributions) => {
      const [attributeKey] = Object.keys(attributeContributions);

      return dataElementIds.some(
        (dataElementId) => dataElementId.split(".")[0] === attributeKey,
      );
    });
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    return this._attributeCollection.getInitialChildModelLinks();
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>, errors: Array<ErrorResponse>) {
    this._attributeCollection.setChildModels(models, errors);
  }

  /**
   * get key
   */
  get key(): string {
    return this.data.objectid;
  }

  /**
   * Get content configuration for form objects
   */
  get contentConfiguration(): ContentConfiguration {
    return this._contentConfiguration;
  }

  /**
   */
  get hasEndResultConfiguration(): boolean {
    return this.contributions.content?.results != null;
  }

  /**
   * Indicates if object is dynamic.
   *
   * A subtle difference exists between objects for wizard style instrument dialogs and other objects.
   * In the 'missing object' response, wizard style objects will include only the first attribute(s) to be answered,
   * while other objects will include the complete set of attributes of that object.
   *
   * This difference is condensed in the term of 'dynamic objects': these objects start (in the 'missing object' response)
   * with a very select set of attributes to be answered, and each successive 'missing elements' response will include next attributes to be answered.
   * When an object is dynamic, the form contributions will mention a "dynamicObject": true property.
   */
  get isDynamic(): boolean {
    return this.getContribution("dynamicObject", false);
  }

  /**
   */
  get hasDynamicValidations(): boolean {
    return this.getContribution("dynamicValidations", false);
  }

  /**
   * Indicates if object is repeatable
   */
  get isRepeatable(): boolean {
    return this.getContribution("repeatable", false);
  }

  /**
   */
  get repeatIndex(): number {
    return this._repeatIndex ?? this.data.index ?? 1;
  }

  /**
   */
  set repeatIndex(repeatIndex: number) {
    this._repeatIndex = repeatIndex;
  }

  /**
   */
  get maxRepeats(): number {
    if (this.isRepeatable) {
      return this.getData("numberofrepeats", -1);
    }

    return 1;
  }

  /**
   */
  get hasFixedNrOfRepeats(): boolean {
    return this.maxRepeats > -1;
  }

  /**
   */
  get isRepeatWithUnknownTotal(): boolean {
    return this.isRepeatable && this.maxRepeats === -1;
  }

  /**
   */
  get isLastRepeat(): boolean {
    if (this.isRepeatable) {
      return this.data.last || false;
    }

    return true;
  }

  /**
   */
  get repeatIndexLabel(): string | null {
    return this.data["index-identifier"] || null;
  }

  /**
   * Get label of form object
   */
  get label(): string {
    return this.contributions.label;
  }

  /**
   * Get introText of form object
   */
  get introText(): string {
    if (this.data.content?.text) {
      return this.data.content.text.message;
    }

    if (this.contributions.text?.message) {
      return this.contributions.text.message;
    }

    return this.contributions.introText;
  }

  /**
   * Get assistent of form object
   */
  get assistent(): string {
    return this.contributions.assistent;
  }

  /**
   * Get button labels
   */
  get buttonLabels(): Object {
    return this.getContribution("buttonLabels", {});
  }

  /**
   * get attribute collection
   */
  get attributeCollection(): AttributeCollection {
    return this._attributeCollection;
  }

  /**
   */
  set attributeCollection(attributeCollection: AttributeCollection) {
    this._attributeCollection = attributeCollection;
  }

  /**
   */
  mergeObject(oldObject: FormObjectModel) {
    this.attributeCollection.forEach((attribute) => {
      const mergeWithAttribute = oldObject.getAttributeByAttribute(attribute);
      if (mergeWithAttribute) {
        attribute.mergeAttribute(mergeWithAttribute);
      }
    });

    this.repeatIndex = oldObject.repeatIndex;
  }

  /**
   * Update attribute
   */
  updateAttribute(attribute: AttributeType, value: any): AttributeType {
    const attributeToUpdate =
      this.attributeCollection.getAttributeByAttribute(attribute);

    if (attributeToUpdate === null) {
      throw new Error(`Attribute with name: ${attribute.name} not found.`);
    }

    if (attributeToUpdate instanceof CompositeAttributeModel) {
      attributeToUpdate.update(value, attribute);
    } else {
      attributeToUpdate.update(value);
    }

    return attributeToUpdate;
  }

  /**
   * Inidicates if Form is changed since a given timestamp (Date.now)
   */
  isChangedSince(timestamp: number): boolean {
    return (
      this.attributeCollection.find((attribute) =>
        attribute.isChangedSince(timestamp),
      ) !== null
    );
  }

  /**
   * Reset all errors on Form Object
   */
  resetErrors() {
    this._errorCollection = new ErrorCollection("formobject");
    this.attributeCollection.forEach((attribute) => attribute.resetErrors());
  }

  /**
   * Get error messages
   */
  get errorCollection(): ErrorCollection {
    return this._errorCollection;
  }

  /**
   * Registers an error that was received from a server response
   */
  addServerError(error: FormErrorAnchor) {
    if (error.anchor?.elementid != null) {
      this.attributeCollection.addServerError(error);
    } else {
      this._errorCollection.addServerError(
        error.id,
        error.message,
        error.properties,
        error.layouthint,
      );
    }
  }

  /**
   */
  hasServerErrors(): boolean {
    return (
      this.errorCollection.hasItems ||
      this.attributeCollection.hasServerErrors()
    );
  }

  /**
   */
  hasErrors(): boolean {
    return (
      this.errorCollection.hasItems || this.attributeCollection.hasErrors()
    );
  }

  /**
   */
  get isValid(): boolean {
    return (
      this.attributeCollection.visible.every(
        (attribute) => attribute.isValid,
      ) && !this.errorCollection.hasItems
    );
  }

  /**
   */
  get dynamicValidationsLoaded(): boolean {
    return this._dynamicValidationsLoaded;
  }

  /**
   */
  set dynamicValidationsLoaded(dynamicValidationsLoaded: boolean) {
    this._dynamicValidationsLoaded = dynamicValidationsLoaded;
  }

  /**
   * Convert error json from a form service to validation messages on the form object and attributes
   */
  handleErrorValidations(errors: Array<ErrorObject>) {
    const attributeErrors = [];

    errors
      .filter((error) => error.anchor?.objectid === this.key)
      .forEach((error) => {
        if (has(error.anchor, "elementid")) {
          attributeErrors.push(error);
        } else {
          this.errorCollection.addServerError(
            error.id,
            error.message,
            error.properties,
            new LayoutHintCollection(error.layouthint),
          );
        }
      });

    this.attributeCollection.updateValidations(attributeErrors);
  }

  /**
   * Convert missing json from a form service to mandatory validation constraints and messages
   */
  handleMissingValidations(
    missing: {
      anchors: Array<
        | {
            elements: Array<{ elementid: string }>,
            objectid?: string,
          }
        | { anchor: { elementid: string, objectid?: string } },
      >,
    },
    errors: Array<ErrorObject>,
  ) {
    const attributeErrors = [];

    for (const anchor of missing.anchors) {
      if (!anchor.objectid || anchor.objectid === this.key) {
        if (Array.isArray(anchor.elements)) {
          for (const element of anchor.elements) {
            attributeErrors.push({
              anchor: { elementid: element.elementid },
              id: "Constraint.Mandatory",
            });
          }
        } else {
          attributeErrors.push({
            anchor,
            id: "Constraint.Mandatory",
          });
        }
      }
    }

    if (Array.isArray(errors)) {
      errors
        .filter(
          (error) =>
            error.id === "Constraint.Mandatory" &&
            error.anchor?.objectid === this.key &&
            has(error.anchor, "elementid"),
        )
        .forEach((error) => {
          attributeErrors.push(error);
        });
    }
    this.attributeCollection.updateValidations(attributeErrors);
  }

  /**
   */
  updateValidations(data: any): FormObjectModel {
    this.resetErrors();

    if (Array.isArray(data.errors)) {
      this.handleErrorValidations(data.errors);
    }

    // missing attribute errors
    if (Array.isArray(data.missing?.anchors)) {
      this.handleMissingValidations(data.missing, data.errors);
    }

    this.dynamicValidationsLoaded = true;

    return this;
  }

  /**
   * Generate formdata object for current formobject based on formdata of attributes
   */
  get formdata(): { [string]: any } | null {
    return this.attributeCollection.formdata;
  }

  /**
   */
  getFormData(validationData: boolean = false): { [string]: any } | null {
    return this.attributeCollection.getFormData(validationData);
  }
}
